import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../ui/Card';
import { Table, Column } from '../ui/Table';
import { api } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#4b5563'];

type RoiPayload = {
  totalInvested: number;
  totalReceived: number;
  overallROI: number;
  byProperty: Array<{
    propertyId: string;
    propertyName: string;
    investmentAmount: number;
    ownershipPct: number;
    totalPaid: number;
    roi: number;
    statementCount: number;
  }>;
  monthlyStacked?: Array<{
    key: string;
    label: string;
    stacks: Array<{ propertyId: string; propertyName: string; investorShare: number }>;
    total: number;
  }>;
};

type Props = {
  investorId: string;
};

export function InvestorRoiTab({ investorId }: Props) {
  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const { data, isLoading, error } = useQuery({
    queryKey: ['investor-roi', investorId],
    queryFn: () => api.getInvestorROI(investorId) as Promise<RoiPayload>,
    enabled: !!investorId,
    staleTime: 30_000,
  });

  const chartData = useMemo(() => {
    const rows = data?.monthlyStacked ?? [];
    if (rows.length === 0) return [];
    const ids = Array.from(
      new Set(rows.flatMap((r) => r.stacks.map((s) => s.propertyId))),
    );
    return rows.map((r) => {
      const row: Record<string, number | string> = { label: r.label };
      ids.forEach((id) => {
        const stack = r.stacks.find((s) => s.propertyId === id);
        row[id] = stack ? Number(stack.investorShare) : 0;
      });
      return row;
    });
  }, [data?.monthlyStacked]);

  const propertyIdToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of data?.monthlyStacked ?? []) {
      for (const s of r.stacks) {
        m.set(s.propertyId, s.propertyName);
      }
    }
    return m;
  }, [data?.monthlyStacked]);

  const stackIds = useMemo(() => {
    return Array.from(
      new Set((data?.monthlyStacked ?? []).flatMap((r) => r.stacks.map((s) => s.propertyId))),
    );
  }, [data?.monthlyStacked]);

  type ByProp = RoiPayload['byProperty'][0] & { id: string };

  const propertyColumns: Column<ByProp>[] = [
    { header: 'Propiedad', accessor: (r) => r.propertyName },
    {
      header: 'Inversión',
      accessor: (r) =>
        canViewPrices ? `$${Number(r.investmentAmount).toLocaleString('es-MX')}` : '—',
    },
    {
      header: '% Part.',
      accessor: (r) => `${Number(r.ownershipPct).toFixed(2)}%`,
    },
    {
      header: 'Total pagado',
      accessor: (r) =>
        canViewPrices ? `$${Number(r.totalPaid).toLocaleString('es-MX')}` : '—',
    },
    {
      header: 'ROI',
      accessor: (r) => `${Number(r.roi).toFixed(2)}%`,
    },
    { header: '# Pagos', accessor: (r) => r.statementCount },
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
        {error instanceof Error ? error.message : 'Error al cargar ROI'}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">ROI</h2>
        <p className="text-sm text-gray-500 mt-1">
          Resumen basado en inversiones activas y participaciones registradas en estados de cuenta.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Total invertido">
          <p className="text-2xl font-bold text-gray-900">
            {canViewPrices ? `$${Number(data.totalInvested).toLocaleString('es-MX')}` : '—'}
          </p>
        </Card>
        <Card title="Total recibido">
          <p className="text-2xl font-bold text-gray-900">
            {canViewPrices ? `$${Number(data.totalReceived).toLocaleString('es-MX')}` : '—'}
          </p>
        </Card>
        <Card title="ROI global">
          <p className="text-2xl font-bold text-primary">{Number(data.overallROI).toFixed(2)}%</p>
        </Card>
      </div>

      <Card title="Por propiedad">
        <Table
          data={data.byProperty.map((r) => ({ ...r, id: r.propertyId }))}
          columns={propertyColumns}
        />
      </Card>

      <Card title="Participación mensual (últimos 12 meses)">
        {chartData.length === 0 || stackIds.length === 0 ? (
          <p className="text-sm text-gray-500">No hay datos suficientes para la gráfica.</p>
        ) : (
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tickFormatter={(v) => (canViewPrices ? `$${Number(v).toLocaleString('es-MX')}` : '—')}
                  width={72}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    canViewPrices ? `$${Number(value).toLocaleString('es-MX')}` : '—',
                    propertyIdToName.get(name) ?? name,
                  ]}
                />
                <Legend
                  formatter={(value) => propertyIdToName.get(value) ?? value}
                />
                {stackIds.map((pid, i) => (
                  <Bar
                    key={pid}
                    dataKey={pid}
                    stackId="a"
                    fill={CHART_COLORS[i % CHART_COLORS.length]}
                    name={pid}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}
